import React, { useState, useEffect } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import { getViewStats, getClickStats, getLeadCount, getConnections } from '../lib/supabase';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FiTrendingUp, FiMousePointer, FiUsers, FiLink } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const EnhancedAnalytics = ({ profileId }) => {
  const { profile } = useProfile();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    views: { total: 0, views_7d: 0 },
    clicks: { total: 0, by_type: {}, by_day: [] },
    leads: { count: 0 },
    connections: { count: 0 },
  });

  useEffect(() => {
    if (profileId) {
      loadAnalytics();
    }
  }, [profileId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [viewsData, clicksData, leadsData, connectionsData] = await Promise.all([
        getViewStats(profileId),
        getClickStats(profileId, 30),
        getLeadCount(profileId),
        getConnections(profileId),
      ]);

      setStats({
        views: viewsData.data || { total: 0, views_7d: 0 },
        clicks: clicksData.data || { total: 0, by_type: {}, by_day: [] },
        leads: leadsData.data || { count: 0 },
        connections: { count: connectionsData.data?.length || 0 },
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-dark p-6 rounded-xl border border-dark-700">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
        </div>
      </div>
    );
  }

  const COLORS = ['#84cc16', '#a3e635', '#bef264', '#d9f99d', '#ecfccb'];

  // Prepare data for charts
  const clicksByTypeData = Object.entries(stats.clicks.by_type || {}).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
  }));

  const clicksByDayData = (stats.clicks.by_day || []).slice(-7).map(({ date, count }) => ({
    date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    clicks: count,
  }));

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Views */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-dark-300">Total Views</h3>
            <SafeIcon icon={FiTrendingUp} className="text-lime-500" />
          </div>
          <p className="text-2xl font-bold text-lime-bright">{stats.views.total || 0}</p>
          <p className="text-xs text-dark-400 mt-1">{stats.views.views_7d || 0} in last 7 days</p>
        </div>

        {/* Total Clicks */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-dark-300">Link Clicks</h3>
            <SafeIcon icon={FiMousePointer} className="text-lime-500" />
          </div>
          <p className="text-2xl font-bold text-lime-bright">{stats.clicks.total || 0}</p>
          <p className="text-xs text-dark-400 mt-1">Last 30 days</p>
        </div>

        {/* Leads */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-dark-300">Leads</h3>
            <SafeIcon icon={FiUsers} className="text-lime-500" />
          </div>
          <p className="text-2xl font-bold text-lime-bright">{stats.leads.count || 0}</p>
          <p className="text-xs text-dark-400 mt-1">Contact submissions</p>
        </div>

        {/* Connections */}
        <div className="glass-dark p-6 rounded-xl border border-dark-700">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-dark-300">Connections</h3>
            <SafeIcon icon={FiLink} className="text-lime-500" />
          </div>
          <p className="text-2xl font-bold text-lime-bright">{stats.connections.count || 0}</p>
          <p className="text-xs text-dark-400 mt-1">Total connections</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clicks by Day */}
        {clicksByDayData.length > 0 && (
          <div className="glass-dark p-6 rounded-xl border border-dark-700">
            <h3 className="text-lg font-semibold text-lime-bright mb-4">Clicks (Last 7 Days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={clicksByDayData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                />
                <Line type="monotone" dataKey="clicks" stroke="#84cc16" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Clicks by Type */}
        {clicksByTypeData.length > 0 && (
          <div className="glass-dark p-6 rounded-xl border border-dark-700">
            <h3 className="text-lg font-semibold text-lime-bright mb-4">Clicks by Block Type</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={clicksByTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {clicksByTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Empty State */}
      {stats.views.total === 0 && stats.clicks.total === 0 && stats.leads.count === 0 && (
        <div className="glass-dark p-12 rounded-xl border border-dark-700 text-center">
          <SafeIcon icon={FiTrendingUp} className="text-4xl text-dark-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-dark-200 mb-2">No Analytics Data Yet</h3>
          <p className="text-dark-400">
            Start sharing your profile to see analytics here!
          </p>
        </div>
      )}
    </div>
  );
};

export default EnhancedAnalytics;

